/* eslint-disable react-hooks/rules-of-hooks */
import { ReactNode, memo, useCallback } from 'react';
import styled from '@emotion/styled';
import { Formik, FormikConfig, FormikHelpers, FormikProps, useFormikContext } from 'formik';
import { Button, Checkbox, Form, InputNumber, Select } from 'antd';

import FormField from 'components/FormField';
import { GroupContainer } from './InsiteForm/share';
import { CheckboxWrapper, FieldWrapper, InlineContainer } from './InsiteForm/UserTagGroupField';
import { T, useTranslate } from '@tolgee/react';

const StyledForm = styled(Form)`
  > * {
    margin-bottom: 16px;
  }
`;

export interface OutsiteFormValues extends Remind.OutsiteConfig {}

type FV = OutsiteFormValues;

export type OutsiteFormHelpers = FormikHelpers<FV>;

type FormRenderChildren<FV> = (nodes: ReactNode, formProps: FormikProps<FV>) => ReactNode;

export interface OutsiteFormProps extends FormikConfig<FV> {
  type: Remind.OutsiteType;
  children?: FormRenderChildren<FV>;
}

const delayLimitMap = {
  MINUTES: {
    max: 60,
    min: 5,
  },
  HOURS: {
    max: 24,
    min: 1,
  },
};

function FormContent(props: { type: Remind.OutsiteType; children?: ReactNode }) {
  const { type, children = null } = props;

  const { t } = useTranslate();

  const { values, setValues } = useFormikContext<FV>();

  const {
    outSiteRemindEnable,
    outSiteRemindViewEnable,
    outSiteRemindViewUnit,
    outSiteRemindSolveEnable,
    outSiteRemindSolveUnit,
  } = values;

  const unitOptions = [
    {
      value: 'HOURS',
      label: t('remind.小时'),
    },
    {
      value: 'MINUTES',
      label: t('remind.分钟'),
    },
  ];

  return (
    <StyledForm layout="vertical">
      <GroupContainer>
        <header>
          <h3>{t('remind.客服站内未查看')}</h3>
        </header>
        <div>
          <FieldWrapper>
            <FormField<FV['outSiteRemindViewDelay']> name="outSiteRemindViewDelay">
              {({ field }) => {
                const { [outSiteRemindViewUnit]: delayLimit } = delayLimitMap;

                return (
                  <>
                    <CheckboxWrapper>
                      <Checkbox
                        disabled={!outSiteRemindEnable}
                        checked={outSiteRemindViewEnable}
                        onChange={({ target: { checked } }) =>
                          setValues({
                            ...values,
                            outSiteRemindViewEnable: checked,
                          })
                        }
                      >
                        {type === 1 ? t('飞书提醒') : t('remind.钉钉提醒')}
                      </Checkbox>
                    </CheckboxWrapper>
                    {outSiteRemindEnable && outSiteRemindViewEnable && (
                      <InlineContainer>
                        <T
                          keyName="remind.客服在n[时间单位]内未查看买家问题，24小时内仅进行1次站外提醒"
                          params={{
                            normal: <span />,
                            input: (
                              <InputNumber
                                precision={0}
                                max={delayLimit.max}
                                min={delayLimit.min}
                                {...field}
                              />
                            ),
                            select: (
                              <Select
                                value={outSiteRemindViewUnit}
                                onChange={(value) => {
                                  setValues({
                                    ...values,
                                    outSiteRemindViewUnit: value,
                                  });
                                }}
                                options={unitOptions}
                                onBlur={field.onBlur}
                              />
                            ),
                          }}
                        />
                      </InlineContainer>
                    )}
                  </>
                );
              }}
            </FormField>
          </FieldWrapper>
        </div>
      </GroupContainer>
      <GroupContainer>
        <header>
          <h3>{t('remind.客服站内未解决')}</h3>
        </header>
        <div>
          <FieldWrapper>
            <FormField<FV['outSiteRemindSolveDelay']> name="outSiteRemindSolveDelay">
              {({ field }) => {
                const { [outSiteRemindSolveUnit]: delayLimit } = delayLimitMap;

                return (
                  <>
                    <CheckboxWrapper>
                      <Checkbox
                        disabled={!outSiteRemindEnable}
                        checked={outSiteRemindSolveEnable}
                        onChange={({ target: { checked } }) =>
                          setValues({
                            ...values,
                            outSiteRemindSolveEnable: checked,
                          })
                        }
                      >
                        {type === 1 ? t('飞书提醒') : t('remind.钉钉提醒')}
                      </Checkbox>
                    </CheckboxWrapper>
                    {outSiteRemindEnable && outSiteRemindSolveEnable && (
                      <InlineContainer>
                        <T
                          keyName="remind.客服在n[时间单位]内未解决买家问题，24小时内仅进行1次站外提醒"
                          params={{
                            normal: <span />,
                            input: (
                              <InputNumber
                                precision={0}
                                max={delayLimit.max}
                                min={delayLimit.min}
                                {...field}
                              />
                            ),
                            select: (
                              <Select
                                value={outSiteRemindSolveUnit}
                                onChange={(value) => {
                                  setValues({
                                    ...values,
                                    outSiteRemindSolveUnit: value,
                                  });
                                }}
                                options={unitOptions}
                                onBlur={field.onBlur}
                              />
                            ),
                          }}
                        />
                      </InlineContainer>
                    )}
                  </>
                );
              }}
            </FormField>
          </FieldWrapper>
        </div>
      </GroupContainer>
      {children}
    </StyledForm>
  );
}

function OutsiteForm(props: OutsiteFormProps) {
  const { type, children, ...otherProps } = props;

  const { t } = useTranslate();

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof FV, string>> = {};

      const {
        outSiteRemindViewDelay,
        outSiteRemindViewUnit,
        outSiteRemindSolveDelay,
        outSiteRemindSolveUnit,
      } = values;

      const unitMap = {
        HOURS: '小时',
        MINUTES: '分钟',
      } as const;

      const { [outSiteRemindViewUnit]: viewDelayLimit } = delayLimitMap;

      if (
        outSiteRemindViewDelay > viewDelayLimit.max ||
        outSiteRemindViewDelay < viewDelayLimit.min
      ) {
        errors.outSiteRemindViewDelay = t('remind.时间范围不得超出 min-max unit', {
          min: viewDelayLimit.min,
          max: viewDelayLimit.max,
          unit: t(`remind.${unitMap[outSiteRemindViewUnit]}`),
        });
      }

      const { [outSiteRemindSolveUnit]: solveDelayLimit } = delayLimitMap;

      if (
        outSiteRemindSolveDelay > solveDelayLimit.max ||
        outSiteRemindSolveDelay < solveDelayLimit.min
      ) {
        errors.outSiteRemindSolveDelay = t('remind.时间范围不得超出 min-max unit', {
          min: solveDelayLimit.min,
          max: solveDelayLimit.max,
          unit: t(`remind.${unitMap[outSiteRemindSolveUnit]}`),
        });
      }

      return errors;
    },
    [t],
  );

  return (
    <Formik enableReinitialize validateOnMount validate={validate} {...otherProps}>
      {(props) => {
        if (children) {
          return children(<FormContent type={type} />, props);
        }

        const { dirty, isSubmitting, handleSubmit } = props;

        return (
          <FormContent type={type}>
            <Button
              type="primary"
              disabled={!dirty}
              loading={isSubmitting}
              onClick={() => handleSubmit()}
            >
              {t('remind.保存')}
            </Button>
          </FormContent>
        );
      }}
    </Formik>
  );
}

export default memo(OutsiteForm);
